import type { StoredServer } from "./persistence";
import {
  resolveVaultTemplateString,
  type VaultReferenceContext,
  type VaultReferenceProvider,
} from "@spuerhund/shared/vault";

const ABSOLUTE_URL_PATTERN = /^https?:\/\//i;

type ServerUrlFields = Pick<StoredServer, "baseUrl" | "graphqlPath" | "restPath">;

function joinBaseAndPath(baseUrl: string, path?: string): string {
  const normalizedBaseUrl = baseUrl.trim().replace(/\/+$/, "");
  const normalizedPath = path?.trim() ?? "";

  if (!normalizedBaseUrl) return normalizedPath;
  if (!normalizedPath) return normalizedBaseUrl;

  return `${normalizedBaseUrl}${normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`}`;
}

export function getGraphQLEndpoint(server: Pick<ServerUrlFields, "baseUrl" | "graphqlPath">): string {
  return joinBaseAndPath(server.baseUrl, server.graphqlPath);
}

export function getRestBaseUrl(server: Pick<ServerUrlFields, "baseUrl" | "restPath">): string {
  return joinBaseAndPath(server.baseUrl, server.restPath);
}

export async function resolveServerUrls(
  server: Pick<ServerUrlFields, "baseUrl" | "graphqlPath" | "restPath">,
  provider?: VaultReferenceProvider,
  context?: VaultReferenceContext,
): Promise<{
  baseUrl: string;
  graphqlPath: string;
  restPath: string;
  graphqlEndpoint: string;
  restBaseUrl: string;
}> {
  const baseUrl = await resolveVaultTemplateString(server.baseUrl, provider, context);
  const graphqlPath = await resolveVaultTemplateString(server.graphqlPath ?? "", provider, context);
  const restPath = await resolveVaultTemplateString(server.restPath ?? "", provider, context);

  return {
    baseUrl,
    graphqlPath,
    restPath,
    graphqlEndpoint: joinBaseAndPath(baseUrl, graphqlPath),
    restBaseUrl: joinBaseAndPath(baseUrl, restPath),
  };
}

export function buildRestRequestUrl(
  server: Pick<ServerUrlFields, "baseUrl" | "restPath">,
  path: string,
): string {
  const trimmedPath = path.trim();
  if (ABSOLUTE_URL_PATTERN.test(trimmedPath)) return trimmedPath;

  const baseUrl = getRestBaseUrl(server).replace(/\/+$/, "");
  if (!baseUrl) return trimmedPath;
  if (!trimmedPath) return baseUrl;

  return `${baseUrl}${trimmedPath.startsWith("/") ? trimmedPath : `/${trimmedPath}`}`;
}
